import * as React from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import classes from "./PersonalInformation.module.css";
import Autocomplete from "@mui/material/Autocomplete";
import {
  getAllJobNames,
  getAllHRSystems,
  validateEmployeeId,
} from "services/centerPointUserServices";
import { useDispatch, useSelector } from "react-redux";
import { addNonHRMSEmployeeActions } from "Store/addNonHRMSEmployee";
import VerifiedIcon from "@mui/icons-material/Verified";
import DangerousIcon from "@mui/icons-material/Dangerous";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import { IconButton, Tooltip } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { format } from "date-fns";

const today = new Date();

const PersonalInformation = (props, ref) => {
  const { errorHandler } = props;
  const [jobNamesArray, setJobNamesArray] = React.useState([]);
  const [inputType, setInputType] = React.useState("text");
  const [hrSystemsArray, setHrSystemsArray] = React.useState([]);
  const [validationError, setValidationError] = React.useState({
    state: false,
    message: "",
    severity: "",
  });
  const dispatch = useDispatch();

  const details = useSelector((state) => state.addNonHRMSEmployee);

  React.useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        await fetchData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);
  const fetchData = async () => {
    try {
      const [jobResponse, hrSystemResponse] = await Promise.all([
        getAllJobNames(),
        getAllHRSystems(),
      ]);

      if (jobResponse?.data?.message === "Success") {
        setJobNamesArray(jobResponse?.data?.data);
      } else {
        errorHandler(true, "Job Names could not be fetched.", "error");
      }

      if (hrSystemResponse?.data?.message === "Success") {
        setHrSystemsArray(hrSystemResponse?.data?.data);
      } else {
        errorHandler(true, "HR Systems could not be fetched.", "error");
      }
    } catch (error) {
      errorHandler(true, "Some internal error occurred.", "error");
    }
  };

  const handleFocus = () => {
    setInputType("date");
  };

  const handleBlur = () => {
    setInputType("text");
  };

  const validation = async (e) => {
    const hrEmployeeId = e.target.value;
    const specialCharsRegex = /[!@#$%^&*(),.?":{}|<>]/; // Regex to match special characters
    const maxLength = 20; // Maximum allowed length

    // Check if hrEmployeeId contains special characters or exceeds the maximum length
    if (
      specialCharsRegex.test(hrEmployeeId) ||
      hrEmployeeId.length > maxLength
    ) {
      setValidationError({
        state: true,
        message:
          "HR Employee ID should not contain special characters and should not exceed 20 characters.",
        severity: "error",
      });
      return;
    }

    try {
      if (hrEmployeeId.trim()) {
        const response = await validateEmployeeId(hrEmployeeId);
        if (!response?.data?.data[0]?.IsError) {
          setValidationError({
            state: true,
            message: "Valid HR Employee Id",
            severity: "success",
          });
        } else {
          setValidationError({
            state: true,
            message: response?.data?.data[0]?.Message,
            severity: "error",
          });
        }
      } else {
        setValidationError({
          state: true,
          message: "HR Employee Id can't be empty",
          severity: "error",
        });
      }
    } catch (error) {
      errorHandler(true, "Some internal error occurred.", "error");
    }
  };
  React.useImperativeHandle(ref, () => {
    return {
      errorReset: () =>
        setValidationError({
          state: false,
          message: "",
          severity: "",
        }),
      throwValidationError: (state, message, severity) => {
        setValidationError({
          state: state,
          message: message,
          severity: severity,
        });
      },
    };
  });

  const onHireDateChange = (e) => {
    const hireDate = new Date(e.target.value);
    if (hireDate.getFullYear() > 2099) {
      return false;
    }
    dispatch(addNonHRMSEmployeeActions.updateHireDate(e.target.value));
  };

  return (
    <>
      <Grid container spacing={1} className={classes.gridContainer}>
        <Grid item xs={3}>
          <TextField
            id="outlined-basic"
            variant="outlined"
            value={details.firstName}
            label="First Name"
            required
            disabled={details.disableElements}
            size="small"
            placeholder="First Name"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            onChange={(e) =>
              dispatch(
                addNonHRMSEmployeeActions.updateFirstName(e.target.value),
              )
            }
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            id="outlined-basic"
            variant="outlined"
            label="Middle name"
            size="small"
            placeholder="Middle Name"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            value={details.middleName}
            disabled={details.disableElements}
            onChange={(e) =>
              dispatch(
                addNonHRMSEmployeeActions.updateMiddleName(e.target.value),
              )
            }
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            id="outlined-basic"
            variant="outlined"
            label="Last Name"
            size="small"
            placeholder="Last Name"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            required
            value={details.lastName}
            disabled={details.disableElements}
            onChange={(e) =>
              dispatch(addNonHRMSEmployeeActions.updateLastName(e.target.value))
            }
          />
        </Grid>
        <Grid item xs={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Hire Date"
              value={details.hireDate === "" ? null : moment(details.hireDate)}
              disabled={details.disableElements}
              onChange={(newValue) => {
                if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                  if (newValue.getFullYear() > 2099) {
                    return false;
                  }
                  dispatch(
                    addNonHRMSEmployeeActions.updateHireDate(
                      format(newValue, "yyyy-MM-dd"),
                    ),
                  );
                }
              }}
              format="yyyy-MM-dd"
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={false}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {details.hireDate && (
                          <Tooltip title={"Clear"}>
                            <IconButton
                              onClick={() =>
                                dispatch(
                                  dispatch(
                                    addNonHRMSEmployeeActions.updateHireDate(
                                      "",
                                    ),
                                  ),
                                )
                              }
                              edge="end"
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Grid container spacing={1} className={classes.gridContainer}>
        <Grid item xs={3}>
          <span>{details.updateMode} </span>
          <TextField
            id="outlined-basic"
            variant="outlined"
            label="HR Employee ID"
            placeholder="HR Employee ID"
            size="small"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            disabled={details.disableElements || details.updateMode}
            onBlur={validation}
            required
            value={details.hrEmployeeId}
            onChange={(e) =>
              dispatch(
                addNonHRMSEmployeeActions.updateHrEmployeeId(
                  e.target.value?.trim(),
                ),
              )
            }
          />
          {!details.disableElements && validationError.state && (
            <div className={classes.hrEmpValidation}>
              <p
                className={
                  validationError.severity === "success"
                    ? classes.success
                    : classes.error
                }
              >
                {validationError.message}
              </p>
              {validationError.severity === "success" ? (
                <VerifiedIcon className={classes.success} />
              ) : (
                <DangerousIcon className={classes.error} />
              )}
            </div>
          )}
        </Grid>
        <Grid item xs={3}>
          <Autocomplete
            disablePortal
            blurOnSelect
            disabled={details.disableElements}
            options={jobNamesArray}
            value={details.jobTitle}
            onChange={(e, value) =>
              dispatch(addNonHRMSEmployeeActions.updateJobTitle(value))
            }
            getOptionLabel={(option) =>
              option?.JobTitle === "" ? "" : `${option?.JobTitle}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job Title"
                placeholder="Job Title"
                size="small"
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Autocomplete
            disablePortal
            disabled={details.disableElements}
            blurOnSelect
            options={hrSystemsArray}
            value={details.hrSystem}
            onChange={(e, value) => {
              dispatch(addNonHRMSEmployeeActions.updateHrSystem(value));
            }}
            getOptionLabel={(option) =>
              option?.Name === "" && option?.CP_HRSystemId === ""
                ? ""
                : `${option?.Name + " (" + option?.CP_HRSystemId + ")"}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="HR Systems"
                placeholder="HR Systems"
                size="small"
                fullWidth
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            id="outlined-basic"
            variant="outlined"
            type="email"
            required
            size="small"
            label="Email"
            placeholder="Email"
            fullWidth
            disabled={details.disableElements}
            value={details.email}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            onChange={(e) =>
              dispatch(addNonHRMSEmployeeActions.updateEmail(e.target.value))
            }
          />
        </Grid>
      </Grid>
    </>
  );
};

export default React.forwardRef(PersonalInformation);
